import React from 'react';
import { useTranslations } from 'next-intl';
import styles from './Features.module.css';
import { Container } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';

const Section = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => (
  <section
    className={className ? `${styles.section} ${className}` : styles.section}
    {...props}
  >
    {children}
  </section>
);

const CardsContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardsContainer}>{children}</div>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <article className={styles.card}>{children}</article>
);

const CardImageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardImageWrapper}>{children}</div>
);

const CardImage = (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img alt="" className={styles.cardImage} {...props} />
);

const CardVideo = (props: React.VideoHTMLAttributes<HTMLVideoElement>) => (
  <video className={styles.cardVideo} {...props} />
);

const TextContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.textContainer}>{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className={styles.cardTitle}>{children}</h3>
);

const CardSubtitle = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.cardSubtitle}>{children}</p>
);

// Feature data type - supports both image and video
type FeatureMedia =
  | { type: 'image'; src: string; altKey: string }
  | { type: 'video'; src: string; poster?: string; altKey?: string };

type FeatureData = {
  id: string;
  titleKey: string;
  subtitleKey: string;
  media: FeatureMedia;
};

// Placeholder data - replace with actual image/video paths and content
const featuresData: FeatureData[] = [
  {
    id: 'feature-1',
    titleKey: 'features.cards.sleekPowerful.title',
    subtitleKey: 'features.cards.sleekPowerful.subtitle',
    media: {
      type: 'image',
      src: '/web_v2/uh-home-v2/placeholders/features/00.png',
      altKey: 'features.cards.sleekPowerful.alt',
    },
  },
  {
    id: 'feature-2',
    titleKey: 'features.cards.precisionSensors.title',
    subtitleKey: 'features.cards.precisionSensors.subtitle',
    media: {
      type: 'image',
      src: '/web_v2/uh-home-v2/placeholders/features/001-new.png',
      altKey: 'features.cards.precisionSensors.alt',
    },
  },
  {
    id: 'feature-3',
    titleKey: 'features.cards.privacy.title',
    subtitleKey: 'features.cards.privacy.subtitle',
    media: {
      type: 'image',
      src: '/web_v2/uh-home-v2/placeholders/features/02.png',
      altKey: 'features.cards.privacy.alt',
    },
  },
];

type FeaturesProps = {
  className?: string;
  htmlId?: string;
};

const Features: React.FC<FeaturesProps> = ({ className, htmlId }) => {
  const t = useTranslations('uhHome');
  return (
    <Section
      className={className}
      id={htmlId}
      role="region"
      aria-labelledby={htmlId ? `${htmlId}-heading` : 'features-heading'}
    >
      <Container>
        <CardsContainer>
          {featuresData.map((feature) => (
            <Card key={feature.id}>
              <CardImageWrapper>
                {feature.media.type === 'image' ? (
                  <CardImage
                    src={getCompressedAssetUrl(feature.media.src)}
                    alt={t(feature.media.altKey)}
                  />
                ) : (
                  <CardVideo
                    src={getCompressedAssetUrl(feature.media.src)}
                    poster={
                      feature.media.poster
                        ? getCompressedAssetUrl(feature.media.poster)
                        : undefined
                    }
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-label={t(feature.media.altKey ?? feature.titleKey)}
                  />
                )}
              </CardImageWrapper>
              <TextContainer>
                <CardTitle>{t(feature.titleKey)}</CardTitle>
                <CardSubtitle>{t(feature.subtitleKey)}</CardSubtitle>
              </TextContainer>
            </Card>
          ))}
        </CardsContainer>
      </Container>
    </Section>
  );
};

export default Features;
